import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useState,
  type ReactNode,
} from 'react';
import type {
  BillingInterval,
  MembershipTierId,
  PricingTier,
  TokenPack,
  TrialStatus,
} from '../types/membership';
import { pricingService } from '../services/membership/pricingService';
import { trialService } from '../services/membership/trialService';
import { tokenService } from '../services/membership/tokenService';
import { readJSON, writeJSON } from '../services/membership/storage';

const TIER_KEY = 'teskedo_membership_tier_v1';

interface StoredMembership {
  tierId: MembershipTierId;
  interval: BillingInterval;
}

interface MembershipContextType {
  tiers: PricingTier[];
  tokenPacks: TokenPack[];
  currentTierId: MembershipTierId;
  currentTier?: PricingTier;
  interval: BillingInterval;
  trial: TrialStatus;
  loading: boolean;
  setInterval: (interval: BillingInterval) => void;
  /** Subscribe to / switch membership tier (mock) and allocate its tokens. */
  upgradeTier: (tierId: MembershipTierId, interval?: BillingInterval) => Promise<void>;
  /** Buy an a-la-carte token pack (mock); credits tokens immediately. */
  purchaseTokenPack: (pack: TokenPack) => Promise<void>;
  refreshTrial: () => void;
}

const MembershipContext = createContext<MembershipContextType | undefined>(undefined);

export function MembershipProvider({ children }: { children: ReactNode }) {
  const stored = readJSON<StoredMembership>(TIER_KEY, { tierId: 'free_trial', interval: 'monthly' });
  const [tiers, setTiers] = useState<PricingTier[]>([]);
  const [tokenPacks, setTokenPacks] = useState<TokenPack[]>([]);
  const [currentTierId, setCurrentTierId] = useState<MembershipTierId>(stored.tierId);
  const [interval, setIntervalState] = useState<BillingInterval>(stored.interval);
  const [trial, setTrial] = useState<TrialStatus>(trialService.getStatus());
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    Promise.all([pricingService.getTiers(), pricingService.getTokenPacks()]).then(([t, p]) => {
      setTiers(t);
      setTokenPacks(p);
      setLoading(false);
    });
  }, []);

  const refreshTrial = useCallback(() => setTrial(trialService.getStatus()), []);

  const setInterval = (next: BillingInterval) => {
    setIntervalState(next);
    writeJSON(TIER_KEY, { tierId: currentTierId, interval: next });
  };

  const upgradeTier = useCallback(
    async (tierId: MembershipTierId, nextInterval?: BillingInterval) => {
      const chosenInterval = nextInterval ?? interval;
      setCurrentTierId(tierId);
      setIntervalState(chosenInterval);
      writeJSON(TIER_KEY, { tierId, interval: chosenInterval });

      const tier = pricingService.getTierById(tierId);
      if (tier && tier.monthlyTokens > 0) {
        // Purchased subscription tokens never expire in this mock.
        await tokenService.earn(tier.monthlyTokens, 'subscription', `${tier.name} monthly tokens`);
      }
    },
    [interval],
  );

  const purchaseTokenPack = useCallback(async (pack: TokenPack) => {
    await tokenService.earn(
      pack.tokens + pack.bonusTokens,
      'purchase',
      `Token pack: ${pack.tokens}${pack.bonusTokens ? ` (+${pack.bonusTokens} bonus)` : ''}`,
    );
  }, []);

  const currentTier = tiers.find((t) => t.id === currentTierId);

  return (
    <MembershipContext.Provider
      value={{
        tiers,
        tokenPacks,
        currentTierId,
        currentTier,
        interval,
        trial,
        loading,
        setInterval,
        upgradeTier,
        purchaseTokenPack,
        refreshTrial,
      }}
    >
      {children}
    </MembershipContext.Provider>
  );
}

export function useMembershipContext() {
  const ctx = useContext(MembershipContext);
  if (!ctx) throw new Error('useMembershipContext must be used within a MembershipProvider');
  return ctx;
}
